import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  styled,
  Paper,
  Grid,
  Alert,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  HelpOutline,
  InfoOutlined
} from '@mui/icons-material';
import { STATUS } from '../type.ts';

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  minWidth: '150px',
  transition: 'all 0.2s',
  boxShadow: theme.shadows[1],
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const VerifyBox = ({
  onVerify,
  label,
  reason,
}: {
  onVerify: (status: STATUS, rejection_message: string) => void;
  label?: string;
  reason?: string | null;
}) => {
  const theme = useTheme();
  const [action, setAction] = useState<STATUS>(STATUS.VERIFICATION_PENDING);
  const [message, setMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChangeAction = (newAction: STATUS) => {
    setAction(newAction);
    if (newAction === STATUS.VERIFIED) {
      setMessage('');
    }
  };

  // Verify the document
  const handleVerifyDocument = async () => {
    setSubmitting(true);
    try {
      await onVerify(action, message);
      setDialogOpen(false);
    } catch (error) {
      console.error('Error verifying document:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const isDisabled = () => {
    if (action === STATUS.VERIFIED) {
      return false;
    } else if (action === STATUS.REJECTED) {
      return message.length === 0;
    }
    return true;
  };

  const openConfirmDialog = () => {
    setDialogOpen(true);
  };

  return (
    <>
      <Box>
        {reason && (
          <Alert
            severity="info"
            icon={<InfoOutlined />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              <strong>Previous rejection reason:</strong> {reason}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              {label ?? 'Document Verification'}
              <Tooltip
                title="Select whether to approve or reject this document. Rejection requires a reason."
                placement="top"
              >
                <HelpOutline
                  fontSize="small"
                  sx={{ ml: 1, verticalAlign: 'middle', opacity: 0.6 }}
                />
              </Tooltip>
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderColor:
                  action === STATUS.VERIFIED
                    ? theme.palette.success.main
                    : undefined,
                borderWidth: action === STATUS.VERIFIED ? 2 : 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => handleChangeAction(STATUS.VERIFIED)}
            >
              <CheckCircle
                color="success"
                fontSize="large"
                sx={{ mb: 1 }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={action === STATUS.VERIFIED ? 700 : 400}
              >
                Approve Document
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                Confirm this document meets all requirements
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderColor:
                  action === STATUS.REJECTED
                    ? theme.palette.error.main
                    : undefined,
                borderWidth: action === STATUS.REJECTED ? 2 : 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => handleChangeAction(STATUS.REJECTED)}
            >
              <Cancel
                color="error"
                fontSize="large"
                sx={{ mb: 1 }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={action === STATUS.REJECTED ? 700 : 400}
              >
                Reject Document
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                Document is invalid or doesn't meet requirements
              </Typography>
            </Paper>
          </Grid>

          {action === STATUS.REJECTED && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rejection Reason"
                variant="outlined"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mt: 2 }}
                multiline
                rows={3}
                placeholder="Please provide a detailed reason for rejecting this document"
                required
                error={action === STATUS.REJECTED && message.length === 0}
                helperText={
                  action === STATUS.REJECTED && message.length === 0
                    ? "Rejection reason is required"
                    : ""
                }
              />
            </Grid>
          )}

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <ActionButton
                variant="contained"
                color={action === STATUS.VERIFIED ? "success" : "error"}
                onClick={openConfirmDialog}
                disabled={isDisabled()}
                startIcon={action === STATUS.VERIFIED ? <CheckCircle /> : <Cancel />}
              >
                {action === STATUS.VERIFIED ? "Approve Document" : "Reject Document"}
              </ActionButton>
            </Grid>
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="verification-dialog-title"
      >
        <DialogTitle id="verification-dialog-title">
          {action === STATUS.VERIFIED ? 'Confirm Approval' : 'Confirm Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {action === STATUS.VERIFIED
              ? 'Are you sure you want to approve this document? This action cannot be undone.'
              : 'Are you sure you want to reject this document with the following reason?'}
          </DialogContentText>

          {action === STATUS.REJECTED && (
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 2, bgcolor: theme.palette.background.default }}
            >
              <Typography variant="body2">{message}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            color="inherit"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyDocument}
            variant="contained"
            color={action === STATUS.VERIFIED ? "success" : "error"}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                Processing...
              </>
            ) : (
              action === STATUS.VERIFIED ? 'Approve' : 'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerifyBox;